import { TelegramLogin } from './TelegramLogin';
import { PricingSection } from './PricingSection';
import { Box, Container, Typography, Card, Fade, Chip, useMediaQuery, useTheme } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import MessageIcon from '@mui/icons-material/Message';
import SecurityIcon from '@mui/icons-material/Security';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import BoltIcon from '@mui/icons-material/Bolt';
import StorageIcon from '@mui/icons-material/Storage';
import ZiaCompassIcon from './icons/ZiaCompassIcon';
import GroupsIcon from '@mui/icons-material/Groups';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
// import WorkIcon from '@mui/icons-material/Work'; // Commented out - Jobs card disabled
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldIcon from '@mui/icons-material/Shield';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TranslateIcon from '@mui/icons-material/Translate';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import NavigationIcon from '@mui/icons-material/Navigation';
import ApiIcon from '@mui/icons-material/Api';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// Extended facility type icons
import ConstructionIcon from '@mui/icons-material/Construction';
import SettingsIcon from '@mui/icons-material/Settings';
import FactoryIcon from '@mui/icons-material/Factory';
import ScienceIcon from '@mui/icons-material/Science';
import PipelineIcon from '@mui/icons-material/LinearScale';
import SpeedIcon from '@mui/icons-material/Speed';
import DeleteIcon from '@mui/icons-material/Delete';
import RecyclingIcon from '@mui/icons-material/Recycling';
import HealingIcon from '@mui/icons-material/Healing';
import VerifiedIcon from '@mui/icons-material/Verified';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AirIcon from '@mui/icons-material/Air';
import AdjustIcon from '@mui/icons-material/Adjust';
import HubIcon from '@mui/icons-material/Hub';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterIcon from '@mui/icons-material/Water';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import LandscapeIcon from '@mui/icons-material/Landscape';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { useTranslation } from '../i18n/translations';
import { useAppStore } from '../stores/appStore';

interface LandingPageProps {
  onAuth: (user: any) => void;
}

export function LandingPage({ onAuth }: LandingPageProps) {
  const { t, language } = useTranslation();
  const toggleLanguage = useAppStore((state) => state.toggleLanguage);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box 
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Video Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          overflow: 'hidden'
        }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.45
          }}
        >
          <source src="/landing-page-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Box>

      {/* Dark Scrim Overlay for Better Content Visibility */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          background: 'radial-gradient(circle at center, rgba(15, 23, 42, 0.3) 0%, rgba(15, 23, 42, 0.7) 100%)',
          pointerEvents: 'none'
        }}
      />

      {/* Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          position: 'relative', 
          zIndex: 10, 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6
        }}
      >
        {/* Top Navigation - Quick Login */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 6,
          pb: 4,
          borderBottom: '1px solid rgba(96, 165, 250, 0.1)'
        }}>
          {/* Telegram Login Widget - Left Side */}
          <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            <TelegramLogin onAuth={onAuth} />
            {/* Language Toggle */}
            <Box
              component="button"
              onClick={toggleLanguage}
              sx={{
                px: 2,
                py: 1,
                backgroundColor: 'rgba(192, 132, 252, 0.2)',
                border: '1px solid rgba(192, 132, 252, 0.5)',
                borderRadius: 2,
                color: '#c084fc',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: 'rgba(192, 132, 252, 0.3)',
                }
              }}
            >
              {language === 'en' ? '🇲🇽 Español' : '🇺🇸 English'}
            </Box>
            {/* Dev Mode Button - for browser testing */}
            <Box
              component="button"
              onClick={() => onAuth({ id: 999999, first_name: 'Dev', username: 'devuser' })}
              sx={{
                px: 2,
                py: 1,
                backgroundColor: 'rgba(250, 204, 21, 0.2)',
                border: '1px solid rgba(250, 204, 21, 0.5)',
                borderRadius: 2,
                color: '#facc15',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(250, 204, 21, 0.3)',
                }
              }}
            >
              🔧 Dev Mode
            </Box>
          </Box>

          {/* Right Side - Existing Bot Link */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: { xs: 'none', sm: 'block' } }}>
              {t('alreadyUsingZia')}
            </Typography>
            <Box
              component="a"
              href="https://t.me/ziawellnavbot"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: isMobile ? 2 : 3,
                py: 1.5,
                backgroundColor: 'rgba(6, 182, 212, 0.2)',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                borderRadius: 1,
                color: '#06b6d4',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'all 0.2s',
                cursor: 'pointer',
                display: 'inline-block',
                '&:hover': {
                  backgroundColor: 'rgba(6, 182, 212, 0.3)',
                  borderColor: 'rgba(6, 182, 212, 0.6)'
                }
              }}
            >
              {isMobile ? '@ziawellnavbot' : t('openBot')}
            </Box>
          </Box>
        </Box>

        {/* Hero Tagline - Competitive Positioning */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#22c55e',
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 800,
                letterSpacing: '0.02em',
                textShadow: '0 0 30px rgba(34, 197, 94, 0.6), 0 0 60px rgba(34, 197, 94, 0.3)',
                fontSize: { xs: '1.5rem', md: '2.2rem' },
                lineHeight: 1.2,
                mb: 2
              }}
            >
              {t('heroTagline')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#06b6d4',
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 600,
                letterSpacing: '0.03em',
                textShadow: '0 0 20px rgba(6, 182, 212, 0.4)',
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                lineHeight: 1.3,
                mb: 3
              }}
            >
              {t('heroSubtagline')}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 500,
                letterSpacing: '0.02em',
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                lineHeight: 1.5
              }}
            >
              {t('heroDescription')}
            </Typography>
          </Box>
        </Fade>

        {/* Feature Cards Section - What Makes Us Different */}
        <Fade in timeout={1200}>
          <Box 
            sx={{ 
              mb: 8,
              maxWidth: '1200px',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
              width: '100%'
            }}
          >
            {/* Voice Navigation Card */}
            <Card 
              elevation={8}
              sx={{ 
                textAlign: 'center',
                p: 3,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(15px)',
                borderRadius: 3,
                border: '2px solid rgba(96, 165, 250, 0.3)',
                boxShadow: '0 0 25px rgba(96, 165, 250, 0.15)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.5), transparent)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  border: '2px solid rgba(96, 165, 250, 0.5)',
                  boxShadow: '0 0 40px rgba(96, 165, 250, 0.3)'
                }
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Chip label={t('premium')} size="small" sx={{ backgroundColor: '#60a5fa', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
              </Box>
              <MicIcon sx={{ fontSize: 52, color: '#60a5fa', mb: 2, filter: 'drop-shadow(0 0 12px rgba(96, 165, 250, 0.4))' }} />
              <Typography variant="h6" sx={{ color: '#60a5fa', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                {t('voiceNavigationTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                {t('voiceNavigationDesc')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(96, 165, 250, 0.7)', fontStyle: 'italic' }}>
                {t('voiceNavigationSubtext')}
              </Typography>
            </Card>

            {/* Telegram Features Card */}
            <Card 
              elevation={8}
              sx={{ 
                textAlign: 'center',
                p: 3,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(15px)',
                borderRadius: 3,
                border: '2px solid rgba(6, 182, 212, 0.3)',
                boxShadow: '0 0 25px rgba(6, 182, 212, 0.15)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.5), transparent)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  border: '2px solid rgba(6, 182, 212, 0.5)',
                  boxShadow: '0 0 40px rgba(6, 182, 212, 0.3)'
                }
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Chip label={t('exclusive')} size="small" sx={{ backgroundColor: '#06b6d4', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
              </Box>
              <MessageIcon sx={{ fontSize: 52, color: '#06b6d4', mb: 2, filter: 'drop-shadow(0 0 12px rgba(6, 182, 212, 0.4))' }} />
              <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                {t('telegramChannelsTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                {t('telegramChannelsDesc')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(6, 182, 212, 0.7)', fontStyle: 'italic' }}>
                {t('telegramChannelsSubtext')}
              </Typography>
            </Card>

            {/* Comprehensive Data Card */}
            <Card 
              elevation={8}
              sx={{ 
                textAlign: 'center',
                p: 3,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(15px)',
                borderRadius: 3,
                border: '2px solid rgba(249, 158, 11, 0.3)',
                boxShadow: '0 0 25px rgba(249, 158, 11, 0.15)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, rgba(249, 158, 11, 0.5), transparent)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  border: '2px solid rgba(249, 158, 11, 0.5)',
                  boxShadow: '0 0 40px rgba(249, 158, 11, 0.3)'
                }
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Chip label={t('chip32Types')} size="small" sx={{ backgroundColor: '#f59e0b', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
              </Box>
              <MapIcon sx={{ fontSize: 52, color: '#f59e0b', mb: 2, filter: 'drop-shadow(0 0 12px rgba(249, 158, 11, 0.4))' }} />
              <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                {t('facilityTypesTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                {t('facilityTypesDesc')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(249, 158, 11, 0.7)', fontStyle: 'italic' }}>
                {t('facilityTypesSubtext')}
              </Typography>
            </Card>
          </Box>
        </Fade>

        {/* Facility Types Showcase - All 32 Types */}
        <Fade in timeout={1400}>
          <Box sx={{ mb: 8, width: '100%', maxWidth: '1200px' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white', 
                fontWeight: 700, 
                mb: 4, 
                textAlign: 'center',
                letterSpacing: '0.03em'
              }}
            >
              {t('searchFacilityTypes')}
            </Typography>
            <Box 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)', md: 'repeat(8, 1fr)' },
                gap: 1.5
              }}
            >
              {[
                // Wells & Rigs - matching SearchResults.tsx exactly
                { icon: LocalGasStationIcon, labelKey: 'well', color: '#f59e0b' },
                { icon: ConstructionIcon, labelKey: 'rig', color: '#fbbf24' },
                { icon: WaterIcon, labelKey: 'swd', color: '#10b981' },
                { icon: OpacityIcon, labelKey: 'brineWell', color: '#0ea5e9' },
                { icon: AdjustIcon, labelKey: 'wellHead', color: '#f59e0b' },
                { icon: AdjustIcon, labelKey: 'wellPad', color: '#f59e0b' },
                // Tanks & Storage
                { icon: BoltIcon, labelKey: 'tankBattery', color: '#06b6d4' },
                { icon: PropaneTankIcon, labelKey: 'gasStorage', color: '#fbbf24' },
                { icon: LandscapeIcon, labelKey: 'belowGrade', color: '#78716c' },
                // Compressors & Pumps
                { icon: SettingsIcon, labelKey: 'compressor', color: '#60a5fa' },
                { icon: AirIcon, labelKey: 'crudePump', color: '#64748b' },
                // Plants & Processing
                { icon: FactoryIcon, labelKey: 'gasPlant', color: '#a855f7' },
                { icon: OilBarrelIcon, labelKey: 'refinery', color: '#a855f7' },
                { icon: ScienceIcon, labelKey: 'treating', color: '#f472b6' },
                { icon: MedicationLiquidIcon, labelKey: 'injection', color: '#34d399' },
                // Flare & Emissions
                { icon: WhatshotIcon, labelKey: 'flareStack', color: '#ef4444' },
                // Pipelines
                { icon: PipelineIcon, labelKey: 'gasLine', color: '#3b82f6' },
                { icon: PipelineIcon, labelKey: 'crudeLine', color: '#64748b' },
                { icon: PipelineIcon, labelKey: 'waterLine', color: '#0ea5e9' },
                // Gathering & Metering
                { icon: HubIcon, labelKey: 'gasGather', color: '#3b82f6' },
                { icon: SpeedIcon, labelKey: 'metering', color: '#8b5cf6' },
                // Waste & Environmental
                { icon: LandscapeIcon, labelKey: 'pits', color: '#78716c' },
                { icon: DeleteIcon, labelKey: 'wasteMgmt', color: '#ef4444' },
                { icon: RecyclingIcon, labelKey: 'recycling', color: '#22c55e' },
                { icon: HealingIcon, labelKey: 'remediate', color: '#eab308' },
                { icon: CleaningServicesIcon, labelKey: 'abatement', color: '#f97316' },
                // Service & Regulatory
                { icon: EngineeringIcon, labelKey: 'serviceCo', color: '#64748b' },
                { icon: VerifiedIcon, labelKey: 'classIII', color: '#34d399' },
                // Generic
                { icon: LocalGasStationIcon, labelKey: 'facility', color: '#f59e0b' },
                { icon: LocationOnIcon, labelKey: 'location', color: '#cbd5e1' },
                // Extra types
                { icon: SpeedIcon, labelKey: 'activeOps', color: '#14b8a6' },
                { icon: MapIcon, labelKey: 'more', color: '#a78bfa' },
              ].map((facility, idx) => {
                const Icon = facility.icon;
                return (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      backgroundColor: 'rgba(15, 23, 42, 0.5)',
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${facility.color}33`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        border: `1px solid ${facility.color}66`,
                        boxShadow: `0 0 15px ${facility.color}30`
                      }
                    }}
                  >
                    <Icon sx={{ fontSize: 24, color: facility.color, mb: 0.5, filter: `drop-shadow(0 0 6px ${facility.color}40)` }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500, fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                      {t(facility.labelKey as any)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Fade>

        {/* Advanced Features Section */}
        <Fade in timeout={1500}>
          <Box sx={{ mb: 8, width: '100%', maxWidth: '1200px' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white', 
                fontWeight: 700, 
                mb: 1, 
                textAlign: 'center',
                letterSpacing: '0.03em'
              }}
            >
              {t('advancedFeaturesTitle')}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                mb: 5, 
                textAlign: 'center',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              {t('advancedFeaturesDesc')}
            </Typography>
            <Box 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 3
              }}
            >
              {/* Voice Search */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(6, 182, 212, 0.3)',
                  boxShadow: '0 0 25px rgba(6, 182, 212, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(6, 182, 212, 0.5)',
                    boxShadow: '0 0 40px rgba(6, 182, 212, 0.3)'
                  }
                }}
              >
                <RecordVoiceOverIcon sx={{ fontSize: 52, color: '#06b6d4', mb: 2, filter: 'drop-shadow(0 0 12px rgba(6, 182, 212, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('voiceSearchTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('voiceSearchDesc')}
                </Typography>
              </Card>

              {/* Drag & Drop Landmarks */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(249, 158, 11, 0.3)',
                  boxShadow: '0 0 25px rgba(249, 158, 11, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(249, 158, 11, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(249, 158, 11, 0.5)',
                    boxShadow: '0 0 40px rgba(249, 158, 11, 0.3)'
                  }
                }}
              >
                <PlaceIcon sx={{ fontSize: 52, color: '#f59e0b', mb: 2, filter: 'drop-shadow(0 0 12px rgba(249, 158, 11, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('dragDropLandmarksTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('dragDropLandmarksDesc')}
                </Typography>
              </Card>

              {/* Oilfield Mapping */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(20, 184, 166, 0.3)',
                  boxShadow: '0 0 25px rgba(20, 184, 166, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(20, 184, 166, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(20, 184, 166, 0.5)',
                    boxShadow: '0 0 40px rgba(20, 184, 166, 0.3)'
                  }
                }}
              >
                <MapIcon sx={{ fontSize: 52, color: '#14b8a6', mb: 2, filter: 'drop-shadow(0 0 12px rgba(20, 184, 166, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#14b8a6', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('oilfieldMappingTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('oilfieldMappingDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(20, 184, 166, 0.8)', fontStyle: 'italic' }}>
                  {t('oilfieldMappingSubtext')}
                </Typography>
              </Card>

              {/* Amigos */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(167, 139, 250, 0.3)',
                  boxShadow: '0 0 25px rgba(167, 139, 250, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(167, 139, 250, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(167, 139, 250, 0.5)',
                    boxShadow: '0 0 40px rgba(167, 139, 250, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <Chip label={t('chipComingSoon')} size="small" sx={{ backgroundColor: '#f59e0b', color: 'white', fontWeight: 600, fontSize: '0.65rem' }} />
                  <Chip label={t('chipOptOut')} size="small" sx={{ backgroundColor: 'rgba(100, 116, 139, 0.8)', color: 'white', fontWeight: 600, fontSize: '0.65rem' }} />
                </Box>
                <PeopleIcon sx={{ fontSize: 52, color: '#a78bfa', mb: 2, filter: 'drop-shadow(0 0 12px rgba(167, 139, 250, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#a78bfa', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('amigosTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('amigosDesc')}
                </Typography>
              </Card>

              {/* Jobs Board & Bidding - COMMENTED OUT
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  boxShadow: '0 0 25px rgba(34, 197, 94, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(34, 197, 94, 0.5)',
                    boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)'
                  }
                }}
              >
                <WorkIcon sx={{ fontSize: 52, color: '#22c55e', mb: 2, filter: 'drop-shadow(0 0 12px rgba(34, 197, 94, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  Jobs & Smart Contracts
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  Post jobs, apply instantly. Company men request services, contractors bid with TON blockchain smart contracts.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(34, 197, 94, 0.8)', fontStyle: 'italic' }}>
                  Powered by Telegram's TON crypto 💎
                </Typography>
              </Card>
              */}

              {/* Road Hazards & Police Sightings */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  boxShadow: '0 0 25px rgba(239, 68, 68, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(239, 68, 68, 0.5)',
                    boxShadow: '0 0 40px rgba(239, 68, 68, 0.3)'
                  }
                }}
              >
                <ReportProblemIcon sx={{ fontSize: 52, color: '#ef4444', mb: 2, filter: 'drop-shadow(0 0 12px rgba(239, 68, 68, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('hazardsAlertsTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('hazardsAlertsDesc')}
                </Typography>
              </Card>

              {/* AI Assistant (Grok) */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(96, 165, 250, 0.3)',
                  boxShadow: '0 0 25px rgba(96, 165, 250, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(96, 165, 250, 0.5)',
                    boxShadow: '0 0 40px rgba(96, 165, 250, 0.3)'
                  }
                }}
              >
                <SmartToyIcon sx={{ fontSize: 52, color: '#60a5fa', mb: 2, filter: 'drop-shadow(0 0 12px rgba(96, 165, 250, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#60a5fa', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('aiAssistantTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('aiAssistantDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(96, 165, 250, 0.8)', fontStyle: 'italic' }}>
                  {t('aiAssistantSubtext')}
                </Typography>
              </Card>

              {/* Privacy-First (Pavel Durov) */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  boxShadow: '0 0 25px rgba(16, 185, 129, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(16, 185, 129, 0.5)',
                    boxShadow: '0 0 40px rgba(16, 185, 129, 0.3)'
                  }
                }}
              >
                <ShieldIcon sx={{ fontSize: 52, color: '#10b981', mb: 2, filter: 'drop-shadow(0 0 12px rgba(16, 185, 129, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('privacyFirstTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('privacyFirstDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(16, 185, 129, 0.8)', fontStyle: 'italic' }}>
                  {t('privacyFirstSubtext')}
                </Typography>
              </Card>

              {/* Community Routes - HANDS-FREE RECORDING */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(244, 114, 182, 0.3)',
                  boxShadow: '0 0 25px rgba(244, 114, 182, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(244, 114, 182, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(244, 114, 182, 0.5)',
                    boxShadow: '0 0 40px rgba(244, 114, 182, 0.3)'
                  }
                }}
              >
                <ZiaCompassIcon sx={{ fontSize: 52, color: '#f472b6', mb: 2, filter: 'drop-shadow(0 0 12px rgba(244, 114, 182, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#f472b6', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('calicheRoutesTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('calicheRoutesDesc')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.8rem' }}>
                  {t('calicheRoutesSubtext')}
                </Typography>
              </Card>

              {/* Wells Near You */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(251, 191, 36, 0.3)',
                  boxShadow: '0 0 25px rgba(251, 191, 36, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(251, 191, 36, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(251, 191, 36, 0.5)',
                    boxShadow: '0 0 40px rgba(251, 191, 36, 0.3)'
                  }
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 52, color: '#fbbf24', mb: 2, filter: 'drop-shadow(0 0 12px rgba(251, 191, 36, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#fbbf24', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('wellsNearYouTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('wellsNearYouDesc')}
                </Typography>
              </Card>

              {/* Weather Data */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(56, 189, 248, 0.3)',
                  boxShadow: '0 0 25px rgba(56, 189, 248, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(56, 189, 248, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(56, 189, 248, 0.5)',
                    boxShadow: '0 0 40px rgba(56, 189, 248, 0.3)'
                  }
                }}
              >
                <ThermostatIcon sx={{ fontSize: 52, color: '#38bdf8', mb: 2, filter: 'drop-shadow(0 0 12px rgba(56, 189, 248, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#38bdf8', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('weatherDataTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('weatherDataDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(56, 189, 248, 0.8)', fontStyle: 'italic' }}>
                  {t('weatherDataSubtext')}
                </Typography>
              </Card>

              {/* Spanish + English */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(192, 132, 252, 0.3)',
                  boxShadow: '0 0 25px rgba(192, 132, 252, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(192, 132, 252, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(192, 132, 252, 0.5)',
                    boxShadow: '0 0 40px rgba(192, 132, 252, 0.3)'
                  }
                }}
              >
                <TranslateIcon sx={{ fontSize: 52, color: '#c084fc', mb: 2, filter: 'drop-shadow(0 0 12px rgba(192, 132, 252, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#c084fc', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('englishSpanishTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('englishSpanishDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(192, 132, 252, 0.8)', fontStyle: 'italic' }}>
                  {t('englishSpanishSubtext')}
                </Typography>
              </Card>

              {/* Crews & Timesheets Feature */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(167, 139, 250, 0.3)',
                  boxShadow: '0 0 25px rgba(167, 139, 250, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(167, 139, 250, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(167, 139, 250, 0.5)',
                    boxShadow: '0 0 40px rgba(167, 139, 250, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <Chip label={t('chipComingSoon')} size="small" sx={{ backgroundColor: '#f59e0b', color: 'white', fontWeight: 600, fontSize: '0.65rem' }} />
                  <Chip label={t('chipOptOut')} size="small" sx={{ backgroundColor: 'rgba(100, 116, 139, 0.8)', color: 'white', fontWeight: 600, fontSize: '0.65rem' }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                  <GroupsIcon sx={{ fontSize: 42, color: '#a78bfa', filter: 'drop-shadow(0 0 12px rgba(167, 139, 250, 0.4))' }} />
                  <AccessTimeIcon sx={{ fontSize: 42, color: '#c084fc', filter: 'drop-shadow(0 0 12px rgba(192, 132, 252, 0.4))' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#a78bfa', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('crewsTimesheetsTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('crewsTimesheetsDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(167, 139, 250, 0.8)', fontStyle: 'italic' }}>
                  {t('crewsTimesheetsSubtext')}
                </Typography>
              </Card>

              {/* Quad Navigation */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  boxShadow: '0 0 25px rgba(34, 197, 94, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(34, 197, 94, 0.5)',
                    boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('chip3Options')} size="small" sx={{ backgroundColor: '#22c55e', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <NavigationIcon sx={{ fontSize: 52, color: '#22c55e', mb: 2, filter: 'drop-shadow(0 0 12px rgba(34, 197, 94, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('quadNavigationTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('quadNavigationDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(34, 197, 94, 0.8)', fontStyle: 'italic' }}>
                  {t('quadNavigationSubtext')}
                </Typography>
              </Card>

              {/* Google APIs */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(96, 165, 250, 0.3)',
                  boxShadow: '0 0 25px rgba(96, 165, 250, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(96, 165, 250, 0.5)',
                    boxShadow: '0 0 40px rgba(96, 165, 250, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('chip7Live30Coming')} size="small" sx={{ backgroundColor: '#60a5fa', color: 'white', fontWeight: 600, fontSize: '0.65rem' }} />
                </Box>
                <ApiIcon sx={{ fontSize: 52, color: '#60a5fa', mb: 2, filter: 'drop-shadow(0 0 12px rgba(96, 165, 250, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#60a5fa', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('googleApisTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('googleApisDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(96, 165, 250, 0.8)', fontStyle: 'italic' }}>
                  {t('googleApisSubtext')}
                </Typography>
              </Card>

              {/* Semi & Hazmat Routing */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(251, 146, 60, 0.3)',
                  boxShadow: '0 0 25px rgba(251, 146, 60, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(251, 146, 60, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(251, 146, 60, 0.5)',
                    boxShadow: '0 0 40px rgba(251, 146, 60, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('chipCDLReady')} size="small" sx={{ backgroundColor: '#fb923c', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <WarningAmberIcon sx={{ fontSize: 52, color: '#fb923c', mb: 2, filter: 'drop-shadow(0 0 12px rgba(251, 146, 60, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#fb923c', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('semiHazmatTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('semiHazmatDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(251, 146, 60, 0.8)', fontStyle: 'italic' }}>
                  {t('semiHazmatSubtext')}
                </Typography>
              </Card>

              {/* Cyber Theme */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(6, 182, 212, 0.3)',
                  boxShadow: '0 0 25px rgba(6, 182, 212, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(6, 182, 212, 0.5)',
                    boxShadow: '0 0 40px rgba(6, 182, 212, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('dayAndNight')} size="small" sx={{ backgroundColor: '#06b6d4', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <AutoAwesomeIcon sx={{ fontSize: 52, color: '#06b6d4', mb: 2, filter: 'drop-shadow(0 0 12px rgba(6, 182, 212, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('cyberThemeTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('cyberThemeDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(6, 182, 212, 0.8)', fontStyle: 'italic' }}>
                  {t('cyberThemeSubtext')}
                </Typography>
              </Card>

              {/* Material Design 3 */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(236, 72, 153, 0.3)',
                  boxShadow: '0 0 25px rgba(236, 72, 153, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(236, 72, 153, 0.5)',
                    boxShadow: '0 0 40px rgba(236, 72, 153, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('chipGoogleM3')} size="small" sx={{ backgroundColor: '#ec4899', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <DesignServicesIcon sx={{ fontSize: 52, color: '#ec4899', mb: 2, filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#ec4899', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('materialDesignTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('materialDesignDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(236, 72, 153, 0.8)', fontStyle: 'italic' }}>
                  {t('materialDesignSubtext')}
                </Typography>
              </Card>

              {/* Cloudflare Edge Network Card */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(249, 115, 22, 0.3)',
                  boxShadow: '0 0 25px rgba(249, 115, 22, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(249, 115, 22, 0.5)',
                    boxShadow: '0 0 40px rgba(249, 115, 22, 0.3)'
                  }
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Chip label={t('chipGlobalCDN')} size="small" sx={{ backgroundColor: '#f97316', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>
                <CloudQueueIcon sx={{ fontSize: 52, color: '#f97316', mb: 2, filter: 'drop-shadow(0 0 12px rgba(249, 115, 22, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#f97316', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('cloudflareTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('cloudflareDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(249, 115, 22, 0.8)', fontStyle: 'italic' }}>
                  {t('cloudflareSubtext')}
                </Typography>
              </Card>

              {/* Real-Time Data Card */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  boxShadow: '0 0 25px rgba(34, 197, 94, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(34, 197, 94, 0.5)',
                    boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)'
                  }
                }}
              >
                <BoltIcon sx={{ fontSize: 52, color: '#22c55e', mb: 2, filter: 'drop-shadow(0 0 12px rgba(34, 197, 94, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('realTimeDataTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('realTimeDataDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(34, 197, 94, 0.7)', fontStyle: 'italic' }}>
                  {t('realTimeDataSubtext')}
                </Typography>
              </Card>

              {/* Download for Offline Card */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(168, 85, 247, 0.3)',
                  boxShadow: '0 0 25px rgba(168, 85, 247, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(168, 85, 247, 0.5)',
                    boxShadow: '0 0 40px rgba(168, 85, 247, 0.3)'
                  }
                }}
              >
                <StorageIcon sx={{ fontSize: 52, color: '#a855f7', mb: 2, filter: 'drop-shadow(0 0 12px rgba(168, 85, 247, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#a855f7', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('offlineTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('offlineDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(168, 85, 247, 0.7)', fontStyle: 'italic' }}>
                  {t('offlineSubtext')}
                </Typography>
              </Card>

              {/* Basin Safety Channels Card */}
              <Card 
                elevation={8}
                sx={{ 
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: 3,
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  boxShadow: '0 0 25px rgba(239, 68, 68, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.5), transparent)'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: '2px solid rgba(239, 68, 68, 0.5)',
                    boxShadow: '0 0 40px rgba(239, 68, 68, 0.3)'
                  }
                }}
              >
                <SecurityIcon sx={{ fontSize: 52, color: '#ef4444', mb: 2, filter: 'drop-shadow(0 0 12px rgba(239, 68, 68, 0.4))' }} />
                <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 700, mb: 2, letterSpacing: '0.05em' }}>
                  {t('basinSafetyTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                  {t('basinSafetyDesc')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(239, 68, 68, 0.7)', fontStyle: 'italic' }}>
                  {t('basinSafetySubtext')}
                </Typography>
              </Card>
            </Box>
          </Box>
        </Fade>

        {/* Login Area - Primary Call to Action */}
        <Fade in timeout={900}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '600px',
            mb: 8,
            mt: 8
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#60a5fa', 
                  mb: 2,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textAlign: 'center'
                }}
              >
                {t('readyToStartTitle')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  mb: 4,
                  lineHeight: 1.6,
                  textAlign: 'center'
                }}
              >
                {t('readyToStartDesc')}
              </Typography>
              <Box id="telegram-login-section" sx={{ 
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Box sx={{ flex: 1, maxWidth: '300px' }}>
                  <TelegramLogin onAuth={onAuth} />
                </Box>
                {/* Dev Mode Button - prominent for browser testing - COMMENTED OUT
                <Box
                  component="button"
                  onClick={() => onAuth({ id: 999999, first_name: 'Dev', username: 'devuser' })}
                  sx={{
                    px: 4,
                    py: 2,
                    backgroundColor: 'rgba(250, 204, 21, 0.2)',
                    border: '2px solid rgba(250, 204, 21, 0.7)',
                    borderRadius: 3,
                    color: '#facc15',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(250, 204, 21, 0.3)',
                      transform: 'scale(1.02)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  🔧 Dev Mode (Skip Login)
                </Box>
                */}
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* New Interactive Pricing Section with Stripe Checkout */}
        <Fade in timeout={1550}>
          <Box sx={{ mb: 6, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <PricingSection />
          </Box>
        </Fade>

        {/* Pricing Card */}
        <Fade in timeout={1600}>
          <Card 
            elevation={8}
            sx={{ 
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '2px solid rgba(6, 182, 212, 0.3)',
              boxShadow: '0 0 30px rgba(6, 182, 212, 0.15)',
              p: 4,
              maxWidth: '500px',
              position: 'relative',
              width: '100%',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.4), transparent)'
              }
            }}
          >
            <Typography variant="h6" sx={{ color: '#06b6d4', mb: 3, textAlign: 'center', fontWeight: 700, textShadow: '0 0 10px rgba(6, 182, 212, 0.3)' }}>
              {t('freeTrialHeading')}
            </Typography>

            {/* Individual Pricing */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, textAlign: 'center', fontWeight: 700 }}>
                {t('forFieldProfessionals')}
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ color: '#60a5fa', fontWeight: 700 }}>
                  {t('individualPrice')}
                  <Typography component="span" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', ml: 0.5 }}>
                    {t('perMonth')}
                  </Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mt: 1 }}>
                  {t('individualSubheading')}
                </Typography>
              </Box>
              <Box sx={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature1')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature2')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature3')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature4')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature5')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature6')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature7')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature8')}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{t('individualFeature9')}</Typography>
              </Box>
            </Box>

            <Box sx={{ my: 3, height: '1px', background: 'rgba(96, 165, 250, 0.2)' }} />

            {/* Business Pricing */}
            <Box>
              <Typography variant="h6" sx={{ color: '#06b6d4', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                {t('businessPlansHeading')}
              </Typography>
              
              {/* Founding 50 Banner */}
              <Box sx={{ 
                mb: 4, 
                p: 2.5, 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.08) 100%)',
                border: '2px solid rgba(251, 191, 36, 0.4)',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ color: '#fbbf24', fontWeight: 700, mb: 1 }}>
                  {t('founding50Heading')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1.5 }}>
                  {t('founding50Desc')}
                </Typography>
                <Typography variant="h4" sx={{ color: '#fbbf24', fontWeight: 800, mb: 1 }}>
                  {t('founding50Price')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                  {t('founding50Locked')}
                </Typography>
                <Box sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, borderRadius: 2, backgroundColor: 'rgba(251, 191, 36, 0.2)' }}>
                  <Typography variant="h6" sx={{ color: '#fbbf24', fontWeight: 700 }}>49</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('founding50Spots')}</Typography>
                </Box>
              </Box>

              {/* 4 Tier Pricing */}
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                {/* Starter */}
                <Box sx={{ textAlign: 'center', flex: '1 1 100px', minWidth: '90px' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, display: 'block', mb: 1, fontSize: '0.7rem' }}>
                    {t('starterLabel')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    {t('starterPrice')}
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5, fontSize: '0.65rem' }}>
                      {t('perUserMonth')}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                    {t('starterRange')}
                  </Typography>
                </Box>
                {/* Team */}
                <Box sx={{ textAlign: 'center', flex: '1 1 100px', minWidth: '90px' }}>
                  <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 700, display: 'block', mb: 1, fontSize: '0.7rem' }}>
                    {t('teamLabel')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#a78bfa', fontWeight: 700 }}>
                    {t('teamPrice')}
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5, fontSize: '0.65rem' }}>
                      {t('perUserMonth')}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                    {t('teamRange')}
                  </Typography>
                </Box>
                {/* Fleet */}
                <Box sx={{ textAlign: 'center', flex: '1 1 100px', minWidth: '90px' }}>
                  <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700, display: 'block', mb: 1, fontSize: '0.7rem' }}>
                    {t('fleetLabel')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                    {t('fleetPrice')}
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5, fontSize: '0.65rem' }}>
                      {t('perUserMonth')}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                    {t('fleetRange')}
                  </Typography>
                </Box>
                {/* Enterprise */}
                <Box sx={{ textAlign: 'center', flex: '1 1 100px', minWidth: '90px' }}>
                  <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 700, display: 'block', mb: 1, fontSize: '0.7rem' }}>
                    {t('enterpriseLabel')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700 }}>
                    {t('enterprisePrice')}
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5, fontSize: '0.65rem' }}>
                      {t('perUserMonth')}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                    {t('enterpriseRange')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', mb: 2, fontWeight: 600 }}>
                {t('allBusinessPlansInclude')}
              </Typography>
              <Box sx={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>{t('businessFeature1')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature2')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature3')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature4')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature5')}</Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature6')}</Typography>
                <Typography variant="body2" sx={{ color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature7')}</Typography>
              </Box>
            </Box>

            {/* Comparison Section */}
            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(96, 165, 250, 0.2)' }}>
              <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                {t('comparisonHeading')}
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mb: 4
              }}>
                {/* Individual Column */}
                <Box sx={{ 
                  p: 3,
                  backgroundColor: 'rgba(96, 165, 250, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(96, 165, 250, 0.2)'
                }}>
                  <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, fontWeight: 700, textAlign: 'center' }}>
                    {t('individualPlanTitle')}
                  </Typography>
                  <Box sx={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature1')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature2')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature3')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature4')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature5')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature6')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature7')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature8')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#60a5fa', fontWeight: 600 }}>{t('individualFeature9')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.4)' }}>{t('excludedFeature1')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.4)' }}>{t('excludedFeature2')}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>{t('excludedFeature3')}</Typography>
                  </Box>
                </Box>

                {/* Business Column */}
                <Box sx={{ 
                  p: 3,
                  backgroundColor: 'rgba(167, 139, 250, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(167, 139, 250, 0.2)'
                }}>
                  <Typography variant="h6" sx={{ color: '#a78bfa', mb: 2, fontWeight: 700, textAlign: 'center' }}>
                    {t('businessPlanTitle')}
                  </Typography>
                  <Box sx={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature1')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature2')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature3')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature4')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature5')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature6')}</Typography>
                    <Typography variant="body2" sx={{ color: '#a78bfa', fontWeight: 600 }}>{t('businessFeature7')}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Data Privacy Section */}
            <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(96, 165, 250, 0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem', lineHeight: 1.6, display: 'block', mb: 2 }}>
                {t('privacyFirstHeading')} <strong>{t('privacyFirstText').split(':')[0]}:</strong> {t('privacyFirstText').split(':')[1]}
              </Typography>
            </Box>
            
            <Typography variant="caption" sx={{ display: 'block', mt: 4, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
              {t('pricingDisclaimer')}
            </Typography>
          </Card>
        </Fade>

        {/* Community Impact Section */}
        <Fade in timeout={1650}>
          <Card
            sx={{
              width: '100%',
              maxWidth: '800px',
              mt: 6,
              p: { xs: 3, sm: 4 },
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.08) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              border: '1px solid rgba(251, 146, 60, 0.3)',
              boxShadow: '0 8px 32px rgba(251, 146, 60, 0.15)'
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#fb923c',
                mb: 1,
                fontSize: { xs: '1.4rem', sm: '1.6rem' }
              }}>
                🧡 {t('communityImpactTitle')}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '1rem'
              }}>
                {t('communityImpactDesc')}
              </Typography>
            </Box>

            {/* Donation Breakdown */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 3,
              mb: 4
            }}>
              {/* Monthly */}
              <Box sx={{ 
                flex: 1,
                p: 3,
                borderRadius: '16px',
                background: 'rgba(251, 146, 60, 0.1)',
                border: '1px solid rgba(251, 146, 60, 0.2)',
                textAlign: 'center'
              }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800, 
                  color: '#fb923c',
                  mb: 0.5
                }}>
                  10%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('monthlyDonation')}
                </Typography>
              </Box>

              {/* Annual */}
              <Box sx={{ 
                flex: 1,
                p: 3,
                borderRadius: '16px',
                background: 'rgba(251, 146, 60, 0.15)',
                border: '1px solid rgba(251, 146, 60, 0.3)',
                textAlign: 'center'
              }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800, 
                  color: '#f97316',
                  mb: 0.5
                }}>
                  20%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('annualDonation')}
                </Typography>
              </Box>
            </Box>

            {/* Packs for Hunger Info */}
            <Box sx={{ 
              p: 3,
              borderRadius: '16px',
              background: 'rgba(34, 197, 94, 0.08)',
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: '#22c55e',
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                🎒 Packs for Hunger — Lea County, NM
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.7,
                mb: 2
              }}>
                {t('packsForHungerDesc')}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                display: 'block',
                fontStyle: 'italic'
              }}>
                {t('quarterlyImpact')}
              </Typography>
            </Box>
          </Card>
        </Fade>

        {/* Footer Section */}
        <Fade in timeout={1700}>
          <Box sx={{ 
            width: '100%',
            mt: 8, 
            pt: 4,
            pb: 4,
            borderTop: '1px solid rgba(96, 165, 250, 0.15)',
            textAlign: 'center'
          }}>
            {/* Login Terms */}
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                mb: 2
              }}
            >
              {t('loginTermsText')}{' '}
              <a href="https://telegram.org/tos" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'rgba(96, 165, 250, 0.7)' }}>{t('termsOfService')}</a>
              {' '}{t('and')}{' '}
              <a href="https://telegram.org/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'rgba(96, 165, 250, 0.7)' }}>{t('privacyPolicy')}</a>
            </Typography>

            {/* Branding + Copyright on same line */}
            <Typography variant="caption" sx={{ 
              color: 'rgba(255, 255, 255, 0.4)', 
              fontSize: '0.8rem'
            }}>
              {t('madeWithLove')}{' '}
              <Typography
                component="a"
                href="https://ziatechs.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(6, 182, 212, 0.8)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('ziatechsLink')}
              </Typography>
              {' · '}{t('copyright')}
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}
